import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InquiryForm from "@/components/InquiryForm";
import DressGallery from "@/components/DressGallery";
import { api } from "@/lib/api";
import { buildWhatsappLink, dressInquiryMessage } from "@/lib/whatsapp";

export default function DressDetail() {
  const { id } = useParams();
  const [dress, setDress] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/dresses/${id}`)
      .then((r) => setDress(r.data))
      .catch(() => setDress(null))
      .finally(() => setLoading(false));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [id]);

  if (loading) {
    return (
      <div className="bg-cream min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 pt-40 pb-32 text-ink/60">Lädt…</div>
      </div>
    );
  }

  if (!dress) {
    return (
      <div className="bg-cream min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 pt-40 pb-32">
          <h1 className="font-display text-5xl">Kleid nicht gefunden</h1>
          <Link to="/kleider" className="mt-6 inline-flex items-center gap-2 link-underline">
            <ArrowLeft size={16} /> Zur Kollektion
          </Link>
        </div>
      </div>
    );
  }

  const waLink = buildWhatsappLink(settings?.whatsapp_number || "", dressInquiryMessage(dress));

  return (
    <div data-testid="dress-detail" className="bg-cream min-h-screen">
      <Navbar />

      <section className="pt-28 md:pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <Link to="/kleider" data-testid="back-to-catalog" className="inline-flex items-center gap-2 text-sm uppercase tracking-widest link-underline mb-10">
            <ArrowLeft size={14} /> Kollektion
          </Link>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
            <div className="relative">
              <DressGallery
                images={(dress.images && dress.images.length ? dress.images : (dress.image_url ? [dress.image_url] : []))}
                alt={dress.name}
              />
              <div className="absolute top-6 left-6 bg-cream/95 backdrop-blur px-4 py-2 z-10">
                <span className="overline">Kleid Nr. {dress.number}</span>
              </div>
            </div>

            <div>
              <div className="overline mb-4">{dress.color} · Größe {dress.size}</div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                {dress.name}
              </h1>

              <div className="mt-10 grid grid-cols-2 gap-y-6 gap-x-10 max-w-md">
                <Spec label="Mietpreis" value={`${dress.price.toFixed(0)} €`} accent />
                {dress.sale_price ? (
                  <Spec label="Kaufpreis" value={`${dress.sale_price.toFixed(0)} €`} />
                ) : (
                  <Spec label="Kauf" value="Nur Miete" />
                )}
                <Spec label="Kaution" value={`${dress.deposit.toFixed(0)} €`} />
                <Spec label="Größe" value={dress.size} />
                <Spec label="Farbe" value={dress.color} />
                <Spec
                  label="Verfügbarkeit"
                  value={dress.available ? "Verfügbar" : "Aktuell vergeben"}
                  icon={dress.available ? <Check size={14} className="text-gold" /> : null}
                />
              </div>

              <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 bg-ink text-cream">
                <span className="text-gold text-lg leading-none">✦</span>
                <span className="text-xs uppercase tracking-[0.28em]">100% Handarbeit · Unikat</span>
              </div>

              {dress.description && (
                <p className="mt-10 text-ink/80 leading-relaxed text-lg font-light max-w-lg">{dress.description}</p>
              )}

              <div className="mt-8 inline-flex items-start gap-3 px-4 py-3 border border-gold/40 bg-white max-w-lg">
                <span className="text-gold font-display text-xl leading-none">✦</span>
                <span className="text-sm text-ink/85">
                  <strong>Kostenlose Anpassung inklusive</strong> – Länge & Weite werden in unserem Schneider-Atelier individuell für dich angepasst.
                </span>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a
                  data-testid="detail-whatsapp"
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-[#25D366] text-white px-8 py-4 text-xs tracking-widest uppercase hover:bg-[#1EBE57] transition-colors duration-300 shadow-sm"
                >
                  <MessageCircle size={16} /> Per WhatsApp anfragen
                </a>
                <a
                  href="#anfrage-form"
                  className="inline-flex items-center justify-center border border-ink text-ink px-8 py-4 text-xs tracking-widest uppercase hover:bg-ink hover:text-cream transition-colors duration-300"
                >
                  Formular nutzen
                </a>
              </div>

              {dress.rental_rules && (
                <div className="mt-14 pt-10 border-t border-[#E5E0D8]">
                  <div className="overline mb-4"><span className="gold-line" />Mietbedingungen</div>
                  <ul className="space-y-3 text-ink/75 leading-relaxed">
                    {dress.rental_rules.split("\n").filter(Boolean).map((line, i) => (
                      <li key={i} className="flex gap-3"><span className="text-gold mt-1">·</span><span>{line}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="anfrage-form" className="py-24 md:py-32 bg-white border-y border-[#E5E0D8]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="overline mb-4"><span className="gold-line" />Anfrage</div>
          <h2 className="font-display text-4xl md:text-5xl">Reserviere {dress.name}.</h2>
          <p className="mt-4 text-ink/70 max-w-md">
            Wir bestätigen deine Anfrage und stimmen einen Termin für die Anprobe ab.
          </p>
          <div className="mt-10">
            <InquiryForm defaultDress={`Kleid ${dress.number} – ${dress.name}`} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Spec({ label, value, accent, icon }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className={`mt-1 flex items-center gap-2 font-display text-2xl ${accent ? "text-gold" : "text-ink"}`}>
        {icon}<span>{value}</span>
      </div>
    </div>
  );
}
